/**
 * Standard Actions configuration for Dawn.
 */

const DAWN_CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification'];

const DAWN_PUBSUB_REFRESHED_SECTIONS = new Set([
  'cart-drawer:cart-drawer',
  'cart-drawer-items:CartDrawer',
  'cart-items:main-cart-items',
]);

function collectCartSections() {
  const sections = new Map();

  for (const el of document.querySelectorAll(DAWN_CART_TAGS.join(','))) {
    let entries;
    try {
      entries = el.getSectionsToRender?.();
    } catch {
      continue;
    }

    const tag = el.tagName.toLowerCase();
    for (const entry of entries ?? []) {
      if (DAWN_PUBSUB_REFRESHED_SECTIONS.has(`${tag}:${entry.id}`)) continue;

      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;

      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;

      const mount = entry.selector
        ? (root.querySelector(entry.selector) ?? (entry.section ? root : null))
        : document.getElementById(entry.id);
      if (!mount) continue;

      sections.set(sectionId, {
        mount,
        extractSelector: entry.selector || '.shopify-section',
      });
    }
  }

  return sections;
}

async function refreshDawnCartUI() {
  const sections = collectCartSections();
  const sectionsQuery = sections.size
    ? `?sections=${[...sections.keys()].join(',')}`
    : '';
  const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
  const url = `${cartUrl}.js${sectionsQuery}`;
  const cartData = await fetch(url, { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);

  if (cartData?.sections) {
    for (const [id, { mount, extractSelector }] of sections) {
      const html = cartData.sections[id];
      if (!html) continue;
      const source = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(extractSelector);
      if (source) mount.replaceChildren(...source.childNodes);
    }
  }

  publish(PUB_SUB_EVENTS.cartUpdate, {
    source: 'external-refresh',
    cartData: cartData ?? undefined,
  });
}

function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.openCart.configure({
    async handler(defaultHandler) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
      return defaultHandler();
    },
  });

  actions.updateCart.configure({
    eventTarget: () => document,
    async handler(defaultHandler) {
      const result = await defaultHandler();
      try {
        await refreshDawnCartUI();
      } catch (error) {
        console.error('[Dawn] Standard Actions cart refresh failed; reloading.', error);
        window.location.reload();
      }
      return result;
    },
  });
}

if (window.Shopify?.actions) {
  initStandardActions();
} else {
  document.addEventListener('DOMContentLoaded', initStandardActions, { once: true });
}

/* CARRIOUS — keep all existing alignment; only button appearance refined to match reference */
(function injectCarriousFinalButtonStyles() {
  const styleId = 'carrious-final-button-styles';
  const old = document.getElementById(styleId);
  if (old) old.remove();

  const css = `
#MainContent[data-template="collection"] #product-grid .grid__item,
#MainContent[data-template="index"] .product-grid .grid__item {
  display: block !important;
}

#MainContent[data-template="collection"] .product-card-wrapper .card:not(.card--horizontal) > .card__content,
#MainContent[data-template="index"] .product-card-wrapper .card:not(.card--horizontal) > .card__content {
  display: flex !important;
  flex-direction: column !important;
  height: auto !important;
  flex: 1 1 auto !important;
  grid-template-rows: none !important;
}

#MainContent[data-template="collection"] .product-card-wrapper .card:not(.card--horizontal) > .card__content > .card__information,
#MainContent[data-template="index"] .product-card-wrapper .card:not(.card--horizontal) > .card__content > .card__information {
  display: flex !important;
  flex-direction: column !important;
  flex: 1 1 auto !important;
  width: 100% !important;
  min-height: 9.8rem !important;
}

#MainContent[data-template="collection"] .product-card-wrapper .card-information,
#MainContent[data-template="index"] .product-card-wrapper .card-information {
  display: flex !important;
  flex-direction: column !important;
  flex: 1 1 auto !important;
}

#MainContent[data-template="collection"] .product-card-wrapper .card__heading,
#MainContent[data-template="index"] .product-card-wrapper .card__heading {
  min-height: 4.2rem !important;
  line-height: 1.35 !important;
  display: -webkit-box !important;
  -webkit-line-clamp: 2 !important;
  -webkit-box-orient: vertical !important;
  overflow: hidden !important;
}

#MainContent[data-template="collection"] .product-card-wrapper .card-information > .price,
#MainContent[data-template="index"] .product-card-wrapper .card-information > .price {
  margin-top: auto !important;
  min-height: 4.6rem !important;
  display: flex !important;
  align-items: flex-end !important;
  justify-content: center !important;
}

#MainContent[data-template="collection"] .product-card-wrapper .quick-add,
#MainContent[data-template="index"] .product-card-wrapper .quick-add {
  width: 100% !important;
  margin-top: auto !important;
  margin-bottom: 1rem !important;
  padding-top: .8rem !important;
}

#MainContent[data-template="collection"] .product-card-wrapper .quick-add__submit,
#MainContent[data-template="index"] .product-card-wrapper .quick-add__submit {
  --buttons-shadow-opacity: 0 !important;
  --buttons-shadow-horizontal-offset: 0px !important;
  --buttons-shadow-vertical-offset: 0px !important;
  --buttons-shadow-blur-radius: 0px !important;
  --border-offset: 0px !important;
  width: 100% !important;
  min-height: 4.7rem !important;
  position: relative !important;
  overflow: hidden !important;
  isolation: isolate !important;
  border-radius: 8px !important;
  border: 1px solid #111 !important;
  background: #fff !important;
  color: #111 !important;
  outline: none !important;
  box-shadow: 0 5px 12px rgba(0,0,0,.08) !important;
  transition:
    color .28s ease,
    border-color .28s ease,
    box-shadow .34s ease,
    transform .28s cubic-bezier(.22,1,.36,1) !important;
}

#MainContent[data-template="collection"] .product-card-wrapper .quick-add__submit::before,
#MainContent[data-template="index"] .product-card-wrapper .quick-add__submit::before {
  content: '' !important;
  position: absolute !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  height: 0 !important;
  background: linear-gradient(to top, #111 0%, #1d1d1d 100%) !important;
  border-radius: inherit !important;
  z-index: -1 !important;
  transition: height .52s cubic-bezier(.22,1,.36,1) !important;
  box-shadow: none !important;
}

#MainContent[data-template="collection"] .product-card-wrapper .quick-add__submit::after,
#MainContent[data-template="index"] .product-card-wrapper .quick-add__submit::after {
  box-shadow: none !important;
  outline: none !important;
  border: 0 !important;
}

@media (hover:hover) and (pointer:fine) {
  #MainContent[data-template="collection"] .product-card-wrapper .quick-add__submit:not([disabled]):hover,
  #MainContent[data-template="index"] .product-card-wrapper .quick-add__submit:not([disabled]):hover {
    color: #fff !important;
    border-color: #111 !important;
    transform: translateY(-1px) !important;
    box-shadow: 0 9px 20px rgba(0,0,0,.11) !important;
  }

  #MainContent[data-template="collection"] .product-card-wrapper .quick-add__submit:not([disabled]):hover::before,
  #MainContent[data-template="index"] .product-card-wrapper .quick-add__submit:not([disabled]):hover::before {
    height: 100% !important;
  }
}

#MainContent[data-template="collection"] .product-card-wrapper .quick-add__submit[disabled],
#MainContent[data-template="index"] .product-card-wrapper .quick-add__submit[disabled] {
  background: rgba(17,17,17,.04) !important;
  border-color: rgba(17,17,17,.18) !important;
  color: rgba(17,17,17,.42) !important;
  box-shadow: none !important;
  transform: none !important;
}

#MainContent[data-template="collection"] .product-card-wrapper .quick-add__submit[disabled]::before,
#MainContent[data-template="index"] .product-card-wrapper .quick-add__submit[disabled]::before {
  display: none !important;
}

@media screen and (max-width: 749px) {
  #MainContent[data-template="collection"] .product-card-wrapper .card:not(.card--horizontal) > .card__content > .card__information,
  #MainContent[data-template="index"] .product-card-wrapper .card:not(.card--horizontal) > .card__content > .card__information {
    min-height: 11.8rem !important;
  }

  #MainContent[data-template="collection"] .product-card-wrapper .card__heading,
  #MainContent[data-template="index"] .product-card-wrapper .card__heading {
    min-height: 4.8rem !important;
    -webkit-line-clamp: 3 !important;
  }

  #MainContent[data-template="collection"] .product-card-wrapper .card-information > .price,
  #MainContent[data-template="index"] .product-card-wrapper .card-information > .price {
    min-height: 4.8rem !important;
  }

  #MainContent[data-template="collection"] .product-card-wrapper .quick-add__submit,
  #MainContent[data-template="index"] .product-card-wrapper .quick-add__submit {
    min-height: 4.4rem !important;
    border-radius: 8px !important;
    font-size: 1.35rem !important;
    box-shadow: 0 4px 10px rgba(0,0,0,.07) !important;
  }
}

@media screen and (min-width: 750px) {
  #MainContent[data-template="collection"] .product-card-wrapper .quick-add__submit,
  #MainContent[data-template="index"] .product-card-wrapper .quick-add__submit {
    min-height: 4.7rem !important;
    font-size: 1.45rem !important;
  }
}
`;

  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = css;
  document.head.appendChild(style);
})();

/* CARRIOUS — collection pages only: premium infinite scroll */
(function carriousCollectionInfiniteScroll() {
  const isCollectionPage = () => {
    const main = document.getElementById('MainContent');
    return main?.dataset?.template === 'collection' || window.location.pathname.includes('/collections/');
  };

  if (!isCollectionPage()) return;

  const styleId = 'carrious-infinite-scroll-styles';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      #MainContent[data-template="collection"] .pagination-wrapper,
      body.template-collection .pagination-wrapper {
        display: none !important;
      }

      .carrious-infinite-scroll {
        width: 100%;
        min-height: 86px;
        display: flex;
        align-items: center;
        justify-content: center;
        clear: both;
      }

      .carrious-infinite-loader {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 7px;
        min-width: 54px;
        min-height: 34px;
        opacity: 0;
        transform: translateY(5px);
        transition: opacity .28s ease, transform .28s ease;
      }

      .carrious-infinite-loader.is-loading {
        opacity: 1;
        transform: translateY(0);
      }

      .carrious-infinite-loader span {
        width: 7px;
        height: 7px;
        border-radius: 999px;
        background: #333;
        opacity: .24;
        transform: translateY(0) scale(.84);
        animation: carriousDotPulse 1.05s cubic-bezier(.4,0,.2,1) infinite;
        box-shadow: 0 3px 9px rgba(0,0,0,.08);
      }

      .carrious-infinite-loader span:nth-child(2) { animation-delay: .14s; }
      .carrious-infinite-loader span:nth-child(3) { animation-delay: .28s; }

      .carrious-infinite-loader[hidden],
      .carrious-infinite-end[hidden] {
        display: none !important;
      }

      .carrious-infinite-end {
        display: block;
        font-size: 1.35rem;
        line-height: 1.4;
        font-weight: 500;
        letter-spacing: .03em;
        color: rgba(18, 18, 18, .58);
        opacity: 0;
        transform: translateY(4px);
        transition: opacity .28s ease, transform .28s ease;
      }

      .carrious-infinite-scroll.is-complete {
        min-height: 70px;
      }

      .carrious-infinite-scroll.is-complete .carrious-infinite-end {
        opacity: 1;
        transform: translateY(0);
      }

      @keyframes carriousDotPulse {
        0%, 70%, 100% { opacity: .24; transform: translateY(0) scale(.84); }
        35% { opacity: 1; transform: translateY(-4px) scale(1); }
      }

      @media (max-width: 749px) {
        .carrious-infinite-scroll { min-height: 68px; }
        .carrious-infinite-scroll.is-complete { min-height: 58px; }
        .carrious-infinite-loader { gap: 6px; }
        .carrious-infinite-loader span { width: 6px; height: 6px; }
        .carrious-infinite-end { font-size: 1.25rem; }
      }

      @media (prefers-reduced-motion: reduce) {
        .carrious-infinite-loader span { animation: none !important; opacity: .65; }
        .carrious-infinite-end { transition: none !important; }
      }
    `;
    document.head.appendChild(style);
  }

  let observer = null;
  let loading = false;
  let nextUrl = null;
  let currentGrid = null;

  function getPageNumber(url) {
    try {
      const parsed = new URL(url, window.location.origin);
      return Number(parsed.searchParams.get('page') || '1');
    } catch {
      return 1;
    }
  }

  function findNextUrl(doc, currentUrl) {
    const currentPage = getPageNumber(currentUrl);
    const links = [...doc.querySelectorAll('.pagination-wrapper a[href], .pagination a[href]')];
    const candidates = links
      .map((a) => ({ href: a.href || a.getAttribute('href'), page: getPageNumber(a.href || a.getAttribute('href')) }))
      .filter((item) => item.href && item.page > currentPage)
      .sort((a, b) => a.page - b.page);
    return candidates[0]?.href || null;
  }

  function ensureSentinel(grid) {
    let sentinel = document.querySelector('.carrious-infinite-scroll');
    if (!sentinel || !sentinel.isConnected) {
      sentinel = document.createElement('div');
      sentinel.className = 'carrious-infinite-scroll';
      sentinel.setAttribute('aria-live', 'polite');
      sentinel.innerHTML = '<div class="carrious-infinite-loader" aria-label="Loading more products"><span></span><span></span><span></span></div><div class="carrious-infinite-end" hidden>No more products</div>';
      const container = grid.closest('#ProductGridContainer') || grid.parentElement;
      container?.appendChild(sentinel);
    }
    return sentinel;
  }

  function showEndState(sentinel) {
    observer?.disconnect();
    const loader = sentinel.querySelector('.carrious-infinite-loader');
    const end = sentinel.querySelector('.carrious-infinite-end');

    loader?.classList.remove('is-loading');
    if (loader) loader.hidden = true;
    if (end) end.hidden = false;

    requestAnimationFrame(() => sentinel.classList.add('is-complete'));
  }

  function prepareLoadingState(sentinel) {
    const loader = sentinel.querySelector('.carrious-infinite-loader');
    const end = sentinel.querySelector('.carrious-infinite-end');

    sentinel.classList.remove('is-complete');
    if (loader) loader.hidden = false;
    if (end) end.hidden = true;
  }

  async function loadMore(sentinel) {
    if (loading || !nextUrl || !currentGrid?.isConnected) return;
    loading = true;
    const loader = sentinel.querySelector('.carrious-infinite-loader');
    loader?.classList.add('is-loading');

    const requestUrl = nextUrl;

    try {
      const response = await fetch(requestUrl, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newGrid = doc.querySelector('#product-grid');

      if (!newGrid) throw new Error('Product grid not found');

      const newItems = [...newGrid.children].filter((node) => node.matches?.('.grid__item'));
      if (!newItems.length) {
        nextUrl = null;
        showEndState(sentinel);
        return;
      }

      const fragment = document.createDocumentFragment();
      newItems.forEach((item) => fragment.appendChild(item));
      currentGrid.appendChild(fragment);

      nextUrl = findNextUrl(doc, requestUrl);

      document.dispatchEvent(new CustomEvent('carrious:products-appended', {
        detail: { count: newItems.length, url: requestUrl }
      }));

      if (!nextUrl) {
        showEndState(sentinel);
      }
    } catch (error) {
      console.error('[CARRIOUS] Infinite scroll load failed:', error);
      observer?.unobserve(sentinel);
      setTimeout(() => {
        if (sentinel.isConnected && nextUrl) observer?.observe(sentinel);
      }, 1500);
    } finally {
      loading = false;
      loader?.classList.remove('is-loading');
    }
  }

  function initInfiniteScroll() {
    if (!isCollectionPage()) return;

    const grid = document.querySelector('#product-grid');
    if (!grid) return;

    if (currentGrid !== grid) {
      observer?.disconnect();
      currentGrid = grid;
      loading = false;
    }

    const pagination = document.querySelector('.pagination-wrapper, .pagination');
    if (pagination) pagination.style.display = 'none';

    nextUrl = findNextUrl(document, window.location.href);

    const sentinel = ensureSentinel(grid);
    if (!nextUrl) {
      showEndState(sentinel);
      return;
    }

    prepareLoadingState(sentinel);

    observer?.disconnect();
    observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) loadMore(sentinel);
    }, {
      root: null,
      rootMargin: '650px 0px',
      threshold: 0.01
    });
    observer.observe(sentinel);
  }

  const start = () => {
    initInfiniteScroll();

    const productGridContainer = document.querySelector('#ProductGridContainer');
    if (productGridContainer && !productGridContainer.dataset.carriousInfiniteWatch) {
      productGridContainer.dataset.carriousInfiniteWatch = 'true';
      const mutationObserver = new MutationObserver(() => {
        window.clearTimeout(window.__carriousInfiniteTimer);
        window.__carriousInfiniteTimer = window.setTimeout(() => {
          const grid = document.querySelector('#product-grid');
          const sentinel = document.querySelector('.carrious-infinite-scroll');
          if (grid && (!sentinel || currentGrid !== grid)) initInfiniteScroll();
        }, 120);
      });
      mutationObserver.observe(productGridContainer, { childList: true, subtree: true });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', () => setTimeout(initInfiniteScroll, 80));
})();
